// The console's Settings › Forecasts screen (console-forecast-settings WEB-FC-01 to -04, VisionPlatform
// weather-forecasts WF-25 to WF-28, P-292 to P-295) over vision-api's getForecastSettings and putForecastSettings.
import type { components } from "../../../api/generated/vision-api";
import { apiClient, unwrap } from "../../../api/client";
import { ApiError } from "../../../api/problem";

export type ForecastSettings = components["schemas"]["ForecastSettings"];
export type ForecastSettingsInput = components["schemas"]["ForecastSettingsInput"];

/** The form's fields as typed; numbers stay text until they are sent. */
export type ForecastForm = {
  enabled: boolean;
  interval_hours: string;
  daily_budget: string;
  overage_cap_eur: string;
};

/** The refresh intervals offered, in hours (60 to 720 minutes on the server; P-294's default is 3). */
export const intervalChoices = [1, 2, 3, 4, 6, 8, 12] as const;

export function formFrom(s: ForecastSettings): ForecastForm {
  return {
    enabled: s.enabled,
    interval_hours: String(s.interval_min / 60),
    daily_budget: String(s.daily_budget),
    overage_cap_eur: String(s.overage_cap_eur),
  };
}

/** What the form would be refused for before it is sent; null when it may go. */
export function formProblem(f: ForecastForm): "budget" | "cap" | null {
  const budget = Number(f.daily_budget);
  if (f.daily_budget.trim() === "" || !Number.isInteger(budget) || budget < 0 || budget > 1_000_000) return "budget";
  const cap = Number(f.overage_cap_eur.replace(",", "."));
  if (f.overage_cap_eur.trim() === "" || !Number.isFinite(cap) || cap < 0 || cap > 10_000) return "cap";
  return null;
}

export function inputFrom(f: ForecastForm): ForecastSettingsInput {
  return {
    enabled: f.enabled,
    interval_min: Math.round(Number(f.interval_hours) * 60),
    daily_budget: Number(f.daily_budget),
    overage_cap_eur: Number(f.overage_cap_eur.replace(",", ".")),
  };
}

/** Calls a day if every place were refreshed at the interval: what the budget has to cover (WF-26). */
export function callsPerDay(places: number, intervalMin: number): number {
  return intervalMin > 0 ? places * Math.ceil((24 * 60) / intervalMin) : 0;
}

/** The share of the day's budget used, 0 to 100, for the meter; 100 when the budget is 0. */
export function budgetShare(calls: number, budget: number): number {
  if (budget <= 0) return 100;
  return Math.min(100, Math.round((calls / budget) * 100));
}

/** Saving needs a platform admin (`staff.manage`, the API's x-scope ops-admin); reading is for any staff. */
export function canEditForecasts(grants: readonly { capability: string }[] | undefined): boolean {
  return (grants ?? []).some((g) => g.capability === "staff.manage");
}

export function forecastError(e: unknown, t: (k: string) => string): string {
  if (!(e instanceof ApiError)) return t("forecastSettings.error.unknown");
  if (e.status === 403) return t("forecastSettings.error.forbidden");
  if (e.status === 501) return t("forecastSettings.error.notYet");
  return e.message;
}

export const forecastSettingsKey = ["ops", "settings", "forecasts"] as const;

export function getForecastSettings(): Promise<ForecastSettings> {
  return unwrap(apiClient("ops").GET("/v1/ops/settings/forecasts"));
}

export function putForecastSettings(body: ForecastSettingsInput): Promise<ForecastSettings> {
  return unwrap(apiClient("ops").PUT("/v1/ops/settings/forecasts", { body }));
}
